import { parseStoredChatLines, readStoredChatLines, unscopedChatStorageKey } from './chatThread.ts';
import type { ChatLine } from './chatThread.ts';

export interface ChatConversation {
  id: string;
  title: string;
  updatedAt: number;
  lines: ChatLine[];
}

export function conversationStorageKey(actor: string): string {
  return `${unscopedChatStorageKey(actor)}:conversations`;
}

export function parseConversations(raw: string | null): ChatConversation[] {
  try {
    const entries: unknown = JSON.parse(raw || '[]');
    if (!Array.isArray(entries)) return [];
    const conversations: ChatConversation[] = [];
    const seen = new Set<string>();
    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') continue;
      const { id, title, updatedAt, lines } = entry;
      if (typeof id !== 'string' || !/^(legacy|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/.test(id) || seen.has(id)) continue;
      if (typeof title !== 'string' || typeof updatedAt !== 'number' || !Number.isFinite(updatedAt)) continue;
      seen.add(id);
      conversations.push({ id, title, updatedAt, lines: parseStoredChatLines(JSON.stringify(lines)) });
    }
    return conversations.sort(byRecentConversation);
  } catch {
    return [];
  }
}

function byRecentConversation(first: ChatConversation, second: ChatConversation): number {
  return second.updatedAt - first.updatedAt;
}

export function readConversations(actor: string): ChatConversation[] {
  try {
    const saved = window.localStorage.getItem(conversationStorageKey(actor));
    if (saved !== null) return parseConversations(saved);
  } catch {
    // Storage may be blocked; keep chat usable for this visit.
  }
  const legacy = readStoredChatLines(unscopedChatStorageKey(actor));
  return legacy.length ? [{ id: 'legacy', title: 'Previous conversation', updatedAt: 0, lines: legacy }] : [];
}

export function saveConversations(actor: string, conversations: ChatConversation[]): boolean {
  try {
    window.localStorage.setItem(conversationStorageKey(actor), JSON.stringify(conversations));
    return true;
  } catch {
    return false;
  }
}

export function updateConversation(conversations: ChatConversation[], id: string, lines: ChatLine[]): ChatConversation[] {
  const previous = conversations.find(conversation => conversation.id === id);
  const firstMessage = lines.find(line => line.role === 'user')?.text.trim();
  const title = id === 'legacy' ? 'Previous conversation' : firstMessage?.slice(0, 70) || 'New chat';
  return [{ ...previous, id, title, lines, updatedAt: Date.now() },
    ...conversations.filter(conversation => conversation.id !== id)].sort(byRecentConversation);
}
